import {
	ReactElement,
	useCallback
} from "react";
import {
	FileSelector,
	FormItem,
	Toolbar,
	ValidationError,
	DimensionRecommendation,
	SupportedImageInfo
} from "client/Components";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import styles from "./FileInput.module.less";
import { BrandingImageConstraints, BrandingLogoModel } from "client/Api/Branding/branding";
import { IValueInputAttachable } from "client/Forms/attachable";

export type FileInputProps = {
	label: string;
	attachable: IValueInputAttachable<BrandingLogoModel>;
	originalImageId: string | null;
	supportedImageTypes: readonly string[];
	canBeCleared?: boolean;
	trackingId?: string;
	supportedFileDetails: BrandingImageConstraints;
	supportedExtensions: readonly string[];
	dimensionRecomendation: DimensionRecommendation;
};

export const FileInput = (props: FileInputProps): ReactElement => {
	const {
		attachable,
		label,
		originalImageId,
		supportedImageTypes,
		canBeCleared = true,
		trackingId,
		supportedFileDetails,
		dimensionRecomendation,
		supportedExtensions
	} = props;

	const src: string | undefined = attachable.value.isSet
		? attachable.value.value?.uri
		: originalImageId !== null
			? getImageUrl(originalImageId)
			: undefined;

	const onClearHandler = useCallback(
		(): void => {
			const value: BrandingLogoModel = {
				isSet: true,
				value: null
			};

			attachable.reducer
				.setValue(value);
			attachable.validation
				.triggerInputFinished();
		},
		[
			attachable.reducer,
			attachable.validation
		]
	);

	return (
		<FormItem label={label}>
			{src && (
				<div>
					<FileSelector
						{...attachable.attachToFileSelector()}
						contentTypes={supportedImageTypes}
					>
						{(onClick) => (
							<img
								{...setTrackingId(trackingId, "image-src")}
								className={styles.image}
								src={src}
								onClick={onClick}
							/>
						)}
					</FileSelector>
				</div>
			)}
			<Toolbar>
				<FileSelector
					{...attachable.attachToFileSelector()}
					contentTypes={supportedImageTypes}
				>
					{(onClick) => (
						<Toolbar.Button
							kind="primary"
							text={localize("_SelectFile")}
							onClick={onClick}
							trackingId={generateTrackingId(trackingId, "_select-button")}
						/>
					)}
				</FileSelector>
				{canBeCleared && isNotNil(src) && (
					<Toolbar.Button
						kind="secondary"
						text={localize("_Clear")}
						onClick={onClearHandler}
						trackingId={generateTrackingId(trackingId, "_clear-button")}
					/>
				)}
			</Toolbar>
			<SupportedImageInfo
				dimensionRecommendation={dimensionRecomendation}
				maxFileSize={supportedFileDetails.maxFileSize}
				supportedFormats={supportedExtensions}
			/>
			<ValidationError
				{...attachable.attachToValidationError()}
			/>
		</FormItem>
	);
};
FileInput.displayName = "FileInput";

function getImageUrl(blobId: string | null): string | undefined {
	return blobId
		? createUrlRelativeToEnvironment(
			`Branding/Logo/?${urlEncodeObject({ id: blobId })}`
		)
		: undefined;
}